/**
 * ManifestDownloadDialog Component
 * Shown when download exceeds 1GB - provides copy-paste command for Mac
 * and download script for Windows, with resume support
 */

import { useState } from "react";
import { Modal, Space, Typography, Button, Alert, message } from "antd";
import { MaterialIcon } from "@/components/MaterialIcon";
import { MODAL_BLUR_STYLES } from "@/styles/modalStyles";
import type { ManifestResult } from "@/services/downloadService";

const { Text, Title, Paragraph } = Typography;

interface ManifestDownloadDialogProps {
  open: boolean;
  manifestResult: ManifestResult;
  onClose: () => void;
}

export function ManifestDownloadDialog({
  open,
  manifestResult,
  onClose,
}: ManifestDownloadDialogProps) {
  const [copiedCommand, setCopiedCommand] = useState(false);

  const { macScriptUrl, winScriptUrl, estimatedSizeGB, fileCount } = manifestResult;

  // Simple command: download the .sh script and run it
  const macCommand = `curl -sS "${macScriptUrl}" | bash`;

  const handleCopyCommand = async () => {
    try {
      await navigator.clipboard.writeText(macCommand);
      setCopiedCommand(true);
      message.success("Command copied to clipboard");
      setTimeout(() => setCopiedCommand(false), 3000);
    } catch {
      message.error("Failed to copy command");
    }
  };

  const formatSize = (gb: number) => {
    if (gb < 1) return `${Math.round(gb * 1024)} MB`;
    return `${gb.toFixed(1)} GB`;
  };

  return (
    <Modal
      title={
        <Space>
          <MaterialIcon name="terminal" />
          <span>Large download — {formatSize(estimatedSizeGB)}</span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      styles={MODAL_BLUR_STYLES}
      width={560}
      footer={
        <Button onClick={onClose}>Close</Button>
      }
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Alert
          type="info"
          showIcon
          message={`${fileCount.toLocaleString()} files — ~${formatSize(estimatedSizeGB)}`}
          description="For downloads this large, use one of the methods below. Already downloaded files will be skipped if you re-run."
        />

        {/* Mac: copy-paste command */}
        <div style={{ border: "1px solid #d9d9d9", borderRadius: 8, padding: 16 }}>
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Space>
              <MaterialIcon name="terminal" size={24} />
              <Title level={5} style={{ margin: 0 }}>Mac</Title>
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Open Terminal (Finder &gt; Applications &gt; Utilities &gt; Terminal), paste this command, and press Enter:
            </Text>
            <div style={{
              background: "#1e1e1e",
              color: "#d4d4d4",
              padding: "12px 16px",
              borderRadius: 6,
              fontSize: 12,
              fontFamily: "monospace",
              overflowX: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
            }}>
              {macCommand}
            </div>
            <Space>
              <Button
                type="primary"
                icon={<MaterialIcon name={copiedCommand ? "check" : "content_copy"} size={16} />}
                onClick={handleCopyCommand}
              >
                {copiedCommand ? "Copied!" : "Copy command"}
              </Button>
              <Button
                size="small"
                type="link"
                href={macScriptUrl}
                target="_blank"
              >
                or download .sh script
              </Button>
            </Space>
          </Space>
        </div>

        {/* Windows download */}
        <div style={{ border: "1px solid #d9d9d9", borderRadius: 8, padding: 16 }}>
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Space>
              <MaterialIcon name="terminal" size={24} />
              <Title level={5} style={{ margin: 0 }}>Windows</Title>
            </Space>
            <Button
              type="primary"
              block
              size="large"
              icon={<MaterialIcon name="download" size={18} />}
              href={winScriptUrl}
              target="_blank"
            >
              Download for Windows (.bat)
            </Button>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Double-click the downloaded file to start. Requires curl (included in Windows 10+).
            </Text>
          </Space>
        </div>

        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
          This download link is valid for 30 days. Already downloaded files will be skipped if you run again.
        </Paragraph>
      </Space>
    </Modal>
  );
}
